import type * as React from 'react';

import { cn } from '@/lib/utils';

// Scroll container for list/report tables that keeps the table's header row
// pinned to the top while the body scrolls.
//
// Why a wrapper instead of a prop on <Table>: `components/ui` is shadcn-managed
// and must not be edited (G9), so the sticky behavior is layered on from here.
//
// How it works: this element is the single scrollport (both axes). The shadcn
// <Table> renders its own `[data-slot=table-container]` with `overflow-x-auto`,
// which would otherwise become a nested scrollport and let the header scroll
// away with the body — so we force it back to `overflow-visible` and let this
// container own all scrolling. The header cells are then pinned with
// `sticky top-0`.
//
// The selectors are scoped to the *direct* child table
// (`& > [data-slot=table-container] > table > thead th`) on purpose: a table can
// contain another <Table> in an expanded row (e.g. the audit diff table), and a
// descendant selector would wrongly neutralize that nested table's horizontal
// scroll and pin its header too. The direct-child path only ever matches the
// outer table.
//
// `bg-background` assumes this container sits on the page background (true for
// every current caller). On a tinted surface, override the header background via
// `className` — twMerge lets a later `bg-*` on the same selector win.
//
// The footer row (`tfoot`, e.g. a grand-totals row) is pinned to the *bottom* of
// the same scrollport, mirroring the sticky header: it stays visible while the
// body scrolls. An opaque `bg-background` (not the shadcn default `bg-muted/50`)
// keeps scrolled rows from bleeding through. Scoped to the direct child table for
// the same nested-table reason as the header selectors above.
//
// Two nested divs, not one: a scrollbar is browser chrome, not box-model
// content, so it doesn't get clipped by `border-radius` on the element that
// scrolls — on Windows especially, its classic (non-overlay) scrollbar
// squares off whichever corner it sits in, right through the rounded card
// edge. The outer div owns the border/radius/`data-surface` and clips via
// `overflow-hidden`; the inner div is the actual scrollport (`overflow-auto`)
// and is what a scrollbar attaches to. The outer's clip then trims the
// scrollbar to the rounded shape instead of the corner reading as cut off.
export function StickyTableScroll({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        // The table is a white card on the cream page. `data-surface="table"`
        // above opts into the shared taupe border that greys on hover — see
        // the table-card block in `styles/globals.css`.
        'min-h-0 flex-1 overflow-hidden rounded-md border light:bg-card',
        className,
      )}
    >
      <div
        className={cn(
          'h-full overflow-auto',
          '[&>[data-slot=table-container]]:overflow-visible',
          // Header is `bg-surface-subtle` — the dedicated "table headers, tab
          // tracks" token (same role PillTabs' list uses), not `bg-muted` —
          // so the header row reads as its own band above the white body
          // rows. Still opaque (a solid hex, not an alpha colour), so
          // scrolled rows still don't bleed through.
          '[&>[data-slot=table-container]>table>thead_th]:sticky [&>[data-slot=table-container]>table>thead_th]:top-0 [&>[data-slot=table-container]>table>thead_th]:z-10 [&>[data-slot=table-container]>table>thead_th]:border-b [&>[data-slot=table-container]>table>thead_th]:bg-surface-subtle',
          // Footer stays `light:bg-card` (white): it must be opaque so
          // scrolled rows don't bleed through, and it's a totals row rather
          // than a header, so it doesn't take the header's cream band.
          '[&>[data-slot=table-container]>table>tfoot_td]:sticky [&>[data-slot=table-container]>table>tfoot_td]:bottom-0 [&>[data-slot=table-container]>table>tfoot_td]:z-10 [&>[data-slot=table-container]>table>tfoot_td]:border-t [&>[data-slot=table-container]>table>tfoot_td]:bg-card',
        )}
        {...props}
      />
    </div>
  );
}
